import type React from 'react'
import { useState, useEffect } from 'react'
import { useDestMaster } from '../hooks/useDestMaster'
import { validateDestMaster, hasErrors } from '../utils/validation'
import type { DestMasterRequest, DestMasterResponse, FormErrors } from '../types/master'

interface DestMasterFormProps {
  destination?: DestMasterResponse | null
  onClose: () => void
  onSuccess: () => void
}

export default function DestMasterForm({ destination, onClose, onSuccess }: DestMasterFormProps) {
  const { createDestination, updateDestination, loading } = useDestMaster()
  const [formData, setFormData] = useState<DestMasterRequest>({
    name: '',
    description: '',
    sort_num: null,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (destination) {
      setFormData({
        name: destination.name,
        description: destination.description || '',
        sort_num: destination.sort_num,
      })
    }
  }, [destination])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    const processedValue = name === 'sort_num' ? (value === '' ? null : Number.parseInt(value, 10)) : value

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleBlur = (field: string) => {
    setTouched(prev => ({
      ...prev,
      [field]: true,
    }))

    // Validate single field
    const fieldErrors = validateDestMaster(formData)
    setErrors(prev => ({
      ...prev,
      [field]: fieldErrors[field] || '',
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields
    const validationErrors = validateDestMaster(formData)
    setErrors(validationErrors)

    if (hasErrors(validationErrors)) {
      return
    }

    try {
      if (destination) {
        await updateDestination(destination.id, formData)
      } else {
        await createDestination(formData)
      }
      onSuccess()
    } catch (error) {
      console.error('Failed to save destination:', error)
    }
  }

  const isEditing = !!destination

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-map-marker-alt me-2" />
              {isEditing ? '運用先を編集' : '新規運用先を追加'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-8">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      名前 <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('name')}
                      placeholder="運用先名を入力"
                      required
                    />
                    {touched.name && errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="mb-3">
                    <label htmlFor="sort_num" className="form-label">
                      並び順
                    </label>
                    <input
                      type="number"
                      className={`form-control ${touched.sort_num && errors.sort_num ? 'is-invalid' : ''}`}
                      id="sort_num"
                      name="sort_num"
                      value={formData.sort_num || ''}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('sort_num')}
                      placeholder="任意"
                      min="0"
                      max="999999"
                    />
                    {touched.sort_num && errors.sort_num && (
                      <div className="invalid-feedback">{errors.sort_num}</div>
                    )}
                    <div className="form-text">表示順の制御に使用</div>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="description" className="form-label">
                  説明
                </label>
                <textarea
                  className={`form-control ${touched.description && errors.description ? 'is-invalid' : ''}`}
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('description')}
                  rows={3}
                  placeholder="説明を入力（任意）"
                />
                {touched.description && errors.description && (
                  <div className="invalid-feedback">{errors.description}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {isEditing ? '更新中...' : '作成中...'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2" />
                    {isEditing ? '更新' : '作成'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
